"use client"

import React from 'react'
import { UseFormReturn } from 'react-hook-form'
import { Receipt } from "lucide-react"
import { Label } from "@/components/ui/label"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { VirtualizedSelect } from "@/components/ui/virtualized-select"
import { Separator } from "@/components/ui/separator"
import { FormSectionCard } from "./FormSectionCard"
import { useLanguage } from "@/contexts/language-context"
import { useFormSectionNavigation } from "@/hooks/use-form-section-navigation"
import { useEngagementLookups } from "@/hooks/use-engagement-lookups"
import { useCodeValues } from "@/hooks/use-code-values"
import MilestoneSection from "./MilestoneSection"
import { RevenueRecognitionSection } from "./RevenueRecognitionSection"
import { CurrencyInput } from "@/components/ui/currency-input"
import { type MilestoneFormData, type BillingSetupFormData, type RevenueRecognitionFormData } from "@/lib/schemas"
import { ENGAGEMENT_BILLING_TYPE, ENGAGEMENT_TYPE } from "@/lib/constants/engagement-codes"
import type { MilestoneTransactionInfo } from '@/types/ui/milestone'
import type { OrganizationSummary } from '@/types/api/engagement'

// Context data for revenue recognition - grouped to reduce prop drilling
export interface RevenueRecognitionContext {
  engagementStartDate: Date | null
  engagementEndDate: Date | null
  engagementName?: string | null
  selectedClient: number
  /** Server-resolved org names — fallback for the client name when the RLS-gated list is empty. */
  organizations?: Record<number, OrganizationSummary>
}

// Milestone action handlers and state - grouped for clarity
export interface MilestoneActionContext {
  onAdd: () => void
  onRemove: (index: number) => void
  onGenerateTransaction?: (milestoneReference: string, amount: number) => void
  canGenerateTransaction?: boolean
  isHandlingAction?: boolean
  currentAction?: string
}

export interface BillingSetupSectionProps {
  form: UseFormReturn<BillingSetupFormData>
  milestoneForm: UseFormReturn<{ milestones: MilestoneFormData[] }>
  revenueRecognitionForm: UseFormReturn<RevenueRecognitionFormData>
  milestoneActions: MilestoneActionContext
  milestoneTransactionInfo?: Record<string, MilestoneTransactionInfo>
  generatingMilestoneRef?: string | null
  isCompleted: boolean
  hiddenSections: Record<string, boolean>
  isReadonly?: boolean
  isChangeRequest?: boolean
  engagementType: string
  currency?: string
  revenueRecognitionContext: RevenueRecognitionContext
  canLockRevenueRecognition?: boolean
}

export const BillingSetupSection = ({
  form,
  milestoneForm,
  revenueRecognitionForm,
  milestoneActions,
  milestoneTransactionInfo,
  generatingMilestoneRef,
  isCompleted,
  hiddenSections,
  isReadonly = false,
  isChangeRequest = false,
  engagementType,
  currency,
  revenueRecognitionContext,
  canLockRevenueRecognition = false,
}: BillingSetupSectionProps) => {
  const { dictionary } = useLanguage()
  const navigationProps = useFormSectionNavigation('billing', hiddenSections)
  const { getByCategoryUnsorted: getEngagementLookupsByCategoryUnsorted } = useEngagementLookups()
  const { getCodeValues: getByCategory } = useCodeValues()

  const watchedValues = form.watch(['billingType'])
  const [billingType] = watchedValues

  const totalAmount = form.watch('totalAmount')

  // Sales tax subdivisions for the dropdown. Region-unlocked (no longer Canada-only) so
  // non-Canadian engagements can select their jurisdiction; whether tax is actually billed is
  // controlled by the separate "Charge sales tax" toggle, not by this field. (GW-562)
  const salesTaxSubdivisions = React.useMemo(() => {
    return getByCategory('geo.CodeSubdivision')
  }, [getByCategory])

  return (
    <FormSectionCard
      id="billing"
      title={dictionary?.engagement?.billing?.title || "Billing"}
      description={dictionary?.engagement?.billing?.description || "Manage billing type and budgets"}
      icon={Receipt}
      isCompleted={isCompleted}
      showPrevNext={true}
      {...navigationProps}
    >
      <Form {...form}>
        {/* Currency and Sales Tax fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField
            control={form.control}
            name="selectedCurrency"
            render={({ field }) => (
              <FormItem className="space-y-2.5">
                <FormLabel className="text-sm font-medium">
                  {dictionary?.engagement?.core?.currency || "Currency"}
                </FormLabel>
                <FormControl>
                  <Select 
                    {...field} 
                    onValueChange={field.onChange} 
                    disabled={isReadonly}
                    allowClear
                  >
                    <SelectTrigger className="form-field-focus">
                      <SelectValue
                        placeholder={dictionary?.engagement?.core?.selectCurrency || "Select currency"}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {getByCategory('geo.CodeCurrency').map(currency => (
                        <SelectItem key={currency.code} value={currency.code!}>
                          {currency.code} - {currency.text}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="selectedSalesTax"
            render={({ field }) => (
              <FormItem className="space-y-2.5">
                <FormLabel className="text-sm font-medium">
                  {dictionary?.engagement?.core?.salesTax || "Sales Tax"}
                </FormLabel>
                <FormControl>
                  <VirtualizedSelect
                    {...field}
                    className="form-field-focus"
                    options={salesTaxSubdivisions}
                    value={field.value || ""}
                    onValueChange={field.onChange}
                    getItemLabel={(subdivision) => subdivision.text}
                    getItemValue={(subdivision) => subdivision.code!}
                    placeholder={dictionary?.engagement?.core?.selectSalesTax || "Select sales tax"}
                    searchPlaceholder="Search subdivisions..."
                    disabled={isReadonly}
                    allowClear
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="chargeSalesTax"
          render={({ field }) => (
            <FormItem className="space-y-0">
              <div className="flex items-center gap-3">
                <FormControl>
                  <Switch
                    checked={!!field.value}
                    onCheckedChange={(checked) => field.onChange(!!checked)}
                    disabled={isReadonly}
                  />
                </FormControl>
                <FormLabel className="text-sm font-medium cursor-pointer mb-0">
                  {dictionary?.engagement?.core?.chargeSalesTax || "Charge Sales Tax"}
                </FormLabel>
              </div>
              <p className="text-xs text-muted-foreground pt-3">
                {dictionary?.engagement?.core?.chargeSalesTaxDescription || "Applies on a go-forward basis"}
              </p>
            </FormItem>
          )}
        />

        <Separator />

        {engagementType !== "" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField
              control={form.control}
              name="billingType"
              render={({ field }) => (
                <FormItem className="space-y-2.5">
                  <FormLabel className="text-sm font-medium">
                    {dictionary?.engagement?.core?.billingType || "Billing Type"}
                  </FormLabel>
                  <FormControl>
                    <RadioGroup
                      {...field}
                      onValueChange={field.onChange}
                      disabled={isReadonly || isChangeRequest || engagementType !== ENGAGEMENT_TYPE.ADVISORY}
                      className="flex gap-6"
                    >
                      {getEngagementLookupsByCategoryUnsorted('engagement.CodeEngagementBillingType').map(billType => (
                        <div key={billType.code} className="flex items-center space-x-2">
                          <RadioGroupItem value={billType.code!} id={billType.code!} />
                          <Label htmlFor={billType.code!}>
                            {billType.value}
                          </Label>
                        </div>
                      ))}
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )}

        {billingType === ENGAGEMENT_BILLING_TYPE.MILESTONE && (
          <MilestoneSection
            form={milestoneForm}
            onMilestoneAdd={milestoneActions.onAdd}
            onMilestoneRemove={milestoneActions.onRemove}
            onGenerateMilestoneTransaction={milestoneActions.onGenerateTransaction}
            canGenerateMilestoneTransaction={milestoneActions.canGenerateTransaction}
            milestoneTransactionInfo={milestoneTransactionInfo}
            generatingMilestoneRef={generatingMilestoneRef}
            isReadonly={isReadonly}
            isHandlingAction={milestoneActions.isHandlingAction}
            currentAction={milestoneActions.currentAction}
            currency={currency}
          />
        )}

        <Separator />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {engagementType !== ENGAGEMENT_TYPE.INTERNAL && (
            <FormField
              control={form.control}
              name="totalAmount"
              render={({ field }) => (
                <FormItem className="space-y-2.5">
                  <FormLabel className="text-sm font-medium">
                    {dictionary?.engagement?.core?.totalAmount || "Contract Amount"}
                  </FormLabel>
                  <FormControl>
                    <CurrencyInput
                      {...field}
                      currency={currency}
                      placeholder={billingType === ENGAGEMENT_BILLING_TYPE.MILESTONE ? "Calculated from milestones" : (dictionary?.engagement?.core?.enterTotalAmount || "Enter contract amount")}
                      className="form-field-focus"
                      readOnly={isReadonly || billingType === ENGAGEMENT_BILLING_TYPE.MILESTONE}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
          <FormField
            control={form.control}
            name="totalInternalBudget"
            render={({ field }) => (
              <FormItem className="space-y-2.5">
                <FormLabel className="text-sm font-medium">
                  {dictionary?.engagement?.core?.totalInternalBudget || "Projected Cost"}
                </FormLabel>
                <FormControl>
                  <CurrencyInput
                    {...field}
                    currency={currency}
                    placeholder={billingType === ENGAGEMENT_BILLING_TYPE.MILESTONE ? "Calculated from milestones" : (dictionary?.engagement?.core?.enterInternalBudget || "Enter projected cost")}
                    className="form-field-focus"
                    readOnly={isReadonly || billingType === ENGAGEMENT_BILLING_TYPE.MILESTONE}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </Form>

      {/* Revenue Recognition Schedule */}
      {billingType === ENGAGEMENT_BILLING_TYPE.MILESTONE && 
       milestoneForm.watch('milestones').length > 0 && (
        <RevenueRecognitionSection
          form={revenueRecognitionForm}
          totalContractValue={totalAmount || 0}
          engagementStartDate={revenueRecognitionContext.engagementStartDate}
          engagementEndDate={revenueRecognitionContext.engagementEndDate}
          engagementName={revenueRecognitionContext.engagementName}
          selectedClient={revenueRecognitionContext.selectedClient}
          organizations={revenueRecognitionContext.organizations}
          currency={currency}
          isReadonly={isReadonly}
          canLockRevenueRecognition={canLockRevenueRecognition}
        />
      )}
    </FormSectionCard>
  )
}

